import axios, { AxiosInstance } from 'axios';
import jwkToPem from 'jwk-to-pem';
import jws from 'jws';
import { DateTime } from 'luxon';
import queryString from 'query-string';
import { AuthenticationCallback, Authenticator } from './Authentication';
import { AuthInfo } from './AuthInfo';
import { getPortalService } from './PortalService';
import { SignInInfo } from './SignInInfo';
import { TokenInfo } from './TokenInfo';
import { UserInfo, UserType } from './UserInfo';

const ERROR_PORTAL_AUTH_NOT_CONFIGURED = 'Portal auth not configured';
const DEFAULT_SECONDS_BEFORE_EXPIRATION: number = 60;

/**
 * An Authenticator instance for use with the CAT Portal
 */
export class PortalAuthentication implements Authenticator {
    appId: string | undefined;
    portalUrl: string | undefined;
    portalService: AxiosInstance | undefined;
    token: TokenInfo | null;
    user: UserInfo | undefined;
    callback: AuthenticationCallback | undefined;

    private userInfoCache: Map<string, UserInfo>;

    /**
     * Creates a new PortalAuthentication instance
     * @param portalUrl The url to the Portal
     * @param appId The appid to use with 
     * @param callback 
     */
    constructor(portalUrl: string | undefined, appId: string | undefined, callback: AuthenticationCallback | undefined) {
        this.portalUrl = portalUrl;
        this.appId = appId;
        this.callback = callback;
        this.token = null;
        if (portalUrl) {
            this.portalService = getPortalService(portalUrl);
        } else {
            console.log('Portal url not set, authentication unusable until configured');
        }
        this.userInfoCache = new Map<string, UserInfo>();

        console.log('Auth instance created');
    }

    /** 
     * Invokes the authentication callback if set
     */
    private invokeCallback = () => {
        if (this.callback) {
            this.callback(this.isAuthenticated());
        }
    }

    /**
     * Gets the signout query parameters that should be passed to portal with the redirect
     * @param currentLocation The current browser location to be sent back to
     * @returns 
     */
    private getSignoutQueryParameters = (currentLocation: Location): string => {
        if (!currentLocation) {
            throw new Error('Cannot get signout query parameters without currentLocation');
        }
        const protocol: string = currentLocation.protocol;
        const hostname: string = currentLocation.hostname;
        const port: string = currentLocation.port ? `:${currentLocation.port}` : '';

        const redirectUri: string = `${protocol}//${hostname}${port}/signout`;

        const queryParams: any = {
            redirectUri: redirectUri
        };

        const queryString: string = Object.keys(queryParams)
            .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(queryParams[k])}`)
            .join('&');

        return queryString;
    }

    /**
     * Gets the query parameters for the sign in redirect to cat portal
     * @param currentLocation the current browsers location that the browser will come back to after sign in
     * @returns A string of query parameters
     */
    private getSignInQueryParameters = (currentLocation: Location): string => {
        if (!currentLocation) {
            throw new Error('Cannot create signin query parameters without redirectionLocation');
        }
        if (!this.appId) {
            throw new Error(ERROR_PORTAL_AUTH_NOT_CONFIGURED);
        }

        const protocol: string = currentLocation.protocol;
        const hostname: string = currentLocation.hostname;
        const port: string = currentLocation.port ? `:${currentLocation.port}` : '';

        const redirectUri: string = `${protocol}//${hostname}${port}/signin`;

        console.log(`Environment: ${process.env.NODE_ENV}`);
        const queryParams: any = {
            redirectUri: redirectUri,
            appId: this.appId,
            state: currentLocation.pathname + currentLocation.search,
        };

        const queryString: string = Object.keys(queryParams)
            .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(queryParams[k])}`)
            .join('&');

        return queryString;
    }

    /**
     * Gets the sign in url with query parameters where the browser will be redirected for sign in
     * @param currentLocation The current browser location where the browser will be taken back to after sign in
     * @returns 
     */
    private getSignInUrl = (currentLocation: Location): string => {
        if (!this.portalUrl) {
            throw new Error(ERROR_PORTAL_AUTH_NOT_CONFIGURED);
        }
        const authenticationUrl = `${this.portalUrl}/sso`;
        const queryParameters = this.getSignInQueryParameters(currentLocation);
        const signInUrl = `${authenticationUrl}?${queryParameters}`;
        return signInUrl;
    }

    /**
     * Gets the sign out url with query parameters where the browsers will be redirected for sign out
     * @param currentLocation The current browsers location where the browser will be taken back to after sign out
     * @returns 
     */
    private getSignoutUrl = (currentLocation: Location): string => {
        if (!this.portalUrl) {
            throw new Error(ERROR_PORTAL_AUTH_NOT_CONFIGURED);
        }
        const queryParameters = this.getSignoutQueryParameters(currentLocation);
        return `${this.portalUrl}/portal/signout?${queryParameters}`;
    }

    /**
     * Gets the JWKS used to verify the JWT access token
     * @returns 
     */
    private getJWKS = async () => {
        if (!this.portalService) {
            throw new Error(ERROR_PORTAL_AUTH_NOT_CONFIGURED);
        }
        const res = await this.portalService.get('/jwks', {
            withCredentials: true
        });
        return res.data;
    }

    /**
     * Verifies the JWT access token from the portal with the JWKS
     * @param token The token to verify
     * @returns An object containing the token and user information
     * @throws An error if the token is not valid
     */
    private verifyToken = async (token: string): Promise<AuthInfo> => {
        try {
            const jwt = jws.decode(token);

            if (!(jwt && jwt.payload)) {
                throw new Error('Missing token payload');
            }

            const payload = JSON.parse(jwt.payload);

            const jwks = await this.getJWKS();
            const certs = jwks.keys.map((jwk: any) => jwkToPem(jwk));
            const verified = certs.some((c: any) => jws.verify(token, jwt.header.alg, c));

            if (!verified) {
                throw new Error('Failed to verify token against JWKS.');
            }

            if (!payload) {
                throw new Error('Failed to extract payload from token');
            }

            const tokenExpiryDateTime: string = DateTime.fromMillis(payload.exp * 1000).toUTC().toISO();

            // For future reference to get the user profile as part of verifying the token
            /*
            const { data: userProfile } = await this.portalService.get('/users/current', {
                withCredentials: true
            });
            */

            const userId = payload['cognito:username'];
            const email = payload.email;
            let displayName = payload.name || `${payload.given_name || ''} ${payload.family_name || ''}`.trim();
            if (!displayName) {
                // If name not provided, use email alias
                displayName = email && email.substr(0, email.indexOf('@'));
            }
            const organizationId = payload['custom:organizationId'];
            const organizationName = payload['custom:organizationName'];
            const authInfo = {
                tokenInfo: new TokenInfo(token, tokenExpiryDateTime),
                userInfo: {
                    userId,
                    email,
                    displayName,
                    organizationId,
                    organizationName,
                },
                //userProfile: userProfile
            };

            return authInfo;
        } catch (err) {
            console.error(err);
            throw err;
        }
    }

    /**
     * Used to configure the authentication instance if not provided in the constructor
     * This can be used for lazy or delayed initialization
     */
    public configure = (portalUrl: string, appId: string, callback?: AuthenticationCallback): void => {
        if (this.portalUrl || this.appId) {
            throw new Error('Portal auth already configured');
        }
        this.portalUrl = portalUrl;
        this.portalService = getPortalService(portalUrl);
        this.appId = appId;
        this.callback = callback;
    }

    /**
     * Signs the authentication instance in using the query parameters provided during redirect from the portal
     * Required query parameters are: id_token (the access token itself)
     * Optional query parameters are: state (the state where the user was before sign in redirect to portal)
     * @param queryParameters A string of query parameters
     * @returns An object containing the access token and the state where the application was before sign in
     */
    public signInWithQueryParameters = async (queryParameters: string): Promise<SignInInfo> => {
        console.log('logging in');
        const queryParams = queryString.parse(queryParameters);
        const token = queryParams.id_token;
        const state = (!Array.isArray(queryParams.state) && (queryParams.state !== null) && (queryParams.state !== '')) ? queryParams.state : undefined;
        if (!token) {
            throw new Error('Authentication Token not found');
        } else if (Array.isArray(token)) {
            throw new Error('Authentication Token must be a string');
        }
        const authInfo = await this.verifyToken(token);
        this.token = authInfo.tokenInfo;
        this.user = authInfo.userInfo;

        this.invokeCallback();

        const signInInfo = {
            authInfo: authInfo,
            state: state
        };

        return signInInfo;
    }

    /**
     * Attempts to refresh the access token from Portal without going through a full sign in redirect
     * @returns An object containing the token
     */
    public refreshToken = async (): Promise<AuthInfo> => {
        console.log('Refreshing token');
        if (!this.appId || !this.portalService) {
            throw new Error(ERROR_PORTAL_AUTH_NOT_CONFIGURED);
        }

        // This required due to a bug in axios that propagates instance defaults settings to global defaults
        // https://github.com/axios/axios/pull/1391

        const commonHeaders = Object.assign({}, axios.defaults.headers.common);
        try {
            const res = await this.portalService.get(`/sso?appId=${this.appId}`);
            const authInfo = await this.verifyToken(res.data);
            this.token = authInfo.tokenInfo;
            this.user = authInfo.userInfo;

            this.invokeCallback();

            return authInfo;
        } catch (e) {
            console.log('Unable to refresh token, full sign in required');
            console.log(e);

            this.invokeCallback();

            throw e;
        } finally {
            axios.defaults.headers.common = commonHeaders;
        }
    }

    /**
     * Redirects the browser for sign out with the portal
     */
    public redirectToSignOut = (): void => {
        console.log('Redirecting for logout');
        const logoutUrl = this.getSignoutUrl(window.location);
        console.log(`Redirecting to ${logoutUrl}`);
        window.location.replace(logoutUrl);
    }

    /**
     * Redirects the browser for sign in with the portal
     */
    public redirectToSignIn = (): void => {
        console.log('Redirecting for signin');
        const signInUrl = this.getSignInUrl(window.location);
        console.log(`Redirecting to ${signInUrl}`);
        window.location.replace(signInUrl);
    }

    /**
     * Gets a fresh access token from the portal, otherwise redirects the browser through the sign in flow
     * This will start a process to guarantee the user is signed in after
     * @returns 
     */
    public authenticate = async (): Promise<AuthInfo> => {
        try {
            const authInfo = await this.refreshToken();
            return authInfo;
        } catch (e) {
            this.redirectToSignIn();
            throw e;
        }
    }

    /**
     * Determines if the authentication instance is signed in based on the presence of a token.
     * @returns true if signed in, false if signed out
     */
    public isAuthenticated = (): boolean => {
        const authenticated: boolean = (this.token !== null && this.token.isValid(DEFAULT_SECONDS_BEFORE_EXPIRATION));
        return authenticated;
    }

    /**
     * Gets the JWT access token from the instance and optionally refreshes the token if required and specified
     * @param refeshIfRequired true if the token should be refreshed if it will no longer be valid
     * @returns The JWT access token string
     */
    public getToken = async (refeshIfRequired = true): Promise<string> => {
        if (!this.token) {
            throw new Error('Not Authenticated');
        }

        // If the token is invalid and the refreshIfRequired flag is set, then refresh the token
        if (refeshIfRequired && !this.token.isValid(DEFAULT_SECONDS_BEFORE_EXPIRATION)) {
            await this.refreshToken();
        }

        return this.token.token;
    }

    /**
     * Gets the name of the authentication provider
     * @returns The name of the provider
     */
    public getProvider = (): string => {
        return 'catportal';
    }

    /**
     * Sets the callback that will be called when a change in sign in state occurs
     * @param callback The callback to invoke
     */
    public setCallback = (callback: AuthenticationCallback): void => {
        this.callback = callback;
    }

    /**
     * Gets the user's info including email if available
     * @returns An object representing user info
     * @throws An error if not authenticated or the email was not given by the provider
     */
    public getUserInfo = (): UserInfo => {

        if (!this.user) {
            throw new Error('Missing user info');
        }

        return this.user;
    }

    /**
     * Gets the user's info including email if available
     * @returns List of objects representing user info
     * @throws An error userIds not provided or portal url not configured
     */
    public getUserInfoList = async (userIds: string[]): Promise<UserInfo[]> => {
        if (!Array.isArray(userIds)) {
            throw new Error('userIds is not an array');
        }

        if(!this.portalService) {
            throw new Error('Portal url not configured');
        }

        const result: UserInfo[] = [];
        const userIdsNotFoundInCache: string[] = [];
        for (const userId of userIds) {
            if (this.userInfoCache.has(userId)) {
                const userProfile = this.userInfoCache.get(userId);
                if (userProfile) {
                    result.push(userProfile);
                }
            }
            else {
                userIdsNotFoundInCache.push(userId);
            }
        }

        if (userIdsNotFoundInCache.length === 0) {
            return result;
        }

        const chunkSize = this.user?.type === UserType.AWS ? 30 : 5;

        const idLists = [...Array(Math.ceil(userIdsNotFoundInCache.length / chunkSize))].map((_, i) =>
            userIdsNotFoundInCache.slice(i * chunkSize, (i + 1) * chunkSize));

        const portalRequest: Promise<UserInfo[]>[] = [];
        const portalCall = (idList: string[]) => this.portalService?.get(`/users?userIds=${idList.join(',')}`);

        const getResultByUserIds = async (idList: string[]): Promise<UserInfo[]> => {
            const response = await portalCall(idList);
            const userInfoList: UserInfo[] = response?.data;
            return userInfoList;
        };

        idLists.forEach(idList => {
            const resultPromise = getResultByUserIds(idList);
            portalRequest.push(resultPromise);
        });

        const portalResponse = await Promise.all(portalRequest);
        portalResponse.forEach(idList => {
            idList.forEach(userInfo => this.userInfoCache.set(userInfo.userId, userInfo));
        });

        return [...portalResponse.flat(), ...result];
    }

    /**
     * Search list of users based on email
     * @returns List of objects representing user info
     * @throws An error if email not provided or portal url not configured
     */
    public searchUserInfoByEmail = async (email: string): Promise<UserInfo[]> => {
        if (!email) {
            throw new Error('email is not provided');
        }

        if(!this.portalService) {
            throw new Error('Portal url not configured');
        }

        const response = await this.portalService.get(`/users?email=${email}`);
        const userInfoList: UserInfo[] = response.data;
        return userInfoList;
    }

}
